import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { Product } from 'teacher-ui-client/utils/products'
import type { Log } from '@blakeelearning/log'
import { getProgressionSequenceUrlForProduct } from 'teacher-ui-client/progression-sequence/utils'
import type { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'

interface MathseedsPrimeSequenceResponseItem {
  curriculumId: string
  threadId: MathseedsPrimeThread
  unitId: number
  lessonId: number
  lessonName: string
  /**
   * MSP gradeId/gradeName is separate to gradePosition and should not be mapped to it.
   */
  gradeName: string
  positionInLesson: number
}
interface SequenceResponses {
  [Product.MSP]?: Record<PropertyKey, MathseedsPrimeSequenceResponseItem>
  [product: string]: unknown
}

export default class ProgressionSequenceService extends Service {
  @service log!: Log

  /**
   * holds progression sequence responses for each product, when they are loaded
   */
  @tracked sequenceResponses: SequenceResponses = {}

  async loadSequence(product: Product) {
    if (this.sequenceResponses[product]) return

    const url = getProgressionSequenceUrlForProduct(product)
    if (!url) return

    const response: unknown = await this.fetchSequence(url)
    if (response) this.sequenceResponses = { ...this.sequenceResponses, [product]: response }
  }

  private async fetchSequence(url: string) {
    try {
      const response = await fetch(url)
      if (response.ok) {
        const result: unknown = await response.json()
        if (result) return result

        this.log.error(`Failed to fetch sequence info; Reason: (empty data)`, url)
      } else {
        this.log.error(`Failed to fetch sequence info; Reason: (${response.status})`, url)
      }
    } catch (e: any) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error(`Failed to fetch sequence info; Reason: (error thrown)`, e, url)
      }
    }
  }

  get mathseedsPrimeSequence() {
    const sequence = this.sequenceResponses[Product.MSP]
    return sequence
  }
}

declare module '@ember/service' {
  interface Registry {
    'progression-sequence': ProgressionSequenceService
  }
}
